import {
  Feature_Base_Bool_Exp,
  Feature_Config,
  Feature_Group_Sorting_Config,
  Feature_Group_Sorting_Config_Bool_Exp,
  Feature_Group_Sorting_Config_Set_Input,
  Feature_Sorting_Config,
  Feature_Sorting_Config_Bool_Exp,
  Feature_Sorting_Config_Set_Input,
  Feature_Type_Sorting_Config,
  Feature_Type_Sorting_Config_Bool_Exp,
  Feature_Type_Sorting_Config_Set_Input,
  Feature_Value,
  Mutation_Root,
  Query_Root,
} from 'src/@types/graphql'
import combinedQuery, { CombinedQueryBuilder } from 'graphql-combine-query'
import {
  queryFeatureTypeSortingConfigs,
  queryFeatureSortingConfigs,
  queryFeatureValuesByFeatureSortingConfig,
  queryFeatureGroupSortingConfig,
  updateSortOnFeatureTypeConfig,
  updateSortOnFeatureGroupConfig,
  updateSortOnFeatureConfig,
} from 'src/screens/shared/feature/featureSortingConfigQueries'
import { Client, useClient } from 'urql'
import { differenceFeaturesList } from 'src/screens/shared/feature/utils/FeatureBaseUtils'
import { Dictionary, groupBy, uniqBy } from 'lodash'
import { LEVEL_TYPE, PROCESS_TYPE } from 'src/shared/constants/constants'

export interface FeatureData {
  featuresTypeConfigs: Query_Root['feature_type_config']
  featureGroupConfigs: Query_Root['feature_group_config']
  featureConfigs: Query_Root['feature_config']

  selectedFeatureValues: Query_Root['feature_value']
  selectedFeatureValuesGroupedByGroupId: Dictionary<Feature_Value[]>

  availableFeatureConfigs: Query_Root['feature_config']
}

export interface NestedFeatureData {
  nestedFeatureData: Map<number, Map<number | null, Feature_Config[]>>
}

const fetchAllFeatureConfigData =
  (urqlClient: Client) =>
  async (
    featureSortingConfigWhere: Feature_Sorting_Config_Bool_Exp,
    featureBaseWhere?: Feature_Base_Bool_Exp,
  ): Promise<FeatureData> => {
    let combinedQueryResult = combinedQuery('FeatureSortingConfigBaseRelatedData') as unknown as CombinedQueryBuilder

    if (featureBaseWhere) {
      // query feature values by process id
      combinedQueryResult = combinedQueryResult.add<
        {
          feature_value: Query_Root['feature_value']
        },
        {
          featureBaseWhere: Feature_Base_Bool_Exp
          featureSortingConfigWhereForSort: Feature_Sorting_Config_Bool_Exp
        }
      >(queryFeatureValuesByFeatureSortingConfig, {
        featureBaseWhere: featureBaseWhere,
        featureSortingConfigWhereForSort: featureSortingConfigWhere,
      })
    }

    // query feature sorting configs (feature_config) for process
    combinedQueryResult = combinedQueryResult
      .add<
        {
          feature_sorting_config: Query_Root['feature_sorting_config']
        },
        {
          featureSortingConfigWhere: Feature_Sorting_Config_Bool_Exp
          ftSortingConfigWhere: Feature_Type_Sorting_Config_Bool_Exp
          fgSortingConfigWhere: Feature_Group_Sorting_Config_Bool_Exp
        }
      >(queryFeatureSortingConfigs, {
        featureSortingConfigWhere: featureSortingConfigWhere,
        ftSortingConfigWhere: featureSortingConfigWhere,
        fgSortingConfigWhere: featureSortingConfigWhere,
      })

      // query feature type sorting configs (feature_type_config) for process
      .add<
        {
          feature_type_sorting_config: Query_Root['feature_type_sorting_config']
        },
        {
          featureTypeSortingConfigWhere: Feature_Type_Sorting_Config_Bool_Exp
        }
      >(queryFeatureTypeSortingConfigs, {
        featureTypeSortingConfigWhere: featureSortingConfigWhere,
      })

      // query feature group sorting configs (feature_group_config) for process
      .add<
        {
          feature_group_sorting_config: Query_Root['feature_group_sorting_config']
        },
        {
          featureGroupSortingConfigWhere: Feature_Group_Sorting_Config_Bool_Exp
        }
      >(queryFeatureGroupSortingConfig, {
        featureGroupSortingConfigWhere: featureSortingConfigWhere,
      })

    const { document, variables } = combinedQueryResult

    const { data, error } = await urqlClient
      .query<{
        feature_value?: Query_Root['feature_value']
        feature_sorting_config: Query_Root['feature_sorting_config']
        feature_type_sorting_config: Query_Root['feature_type_sorting_config']
        feature_group_sorting_config: Query_Root['feature_group_sorting_config']
      }>(document, variables)
      .toPromise()

    if (error || data === undefined) {
      return Promise.reject()
    }

    // per process
    let selectedFeaturesValues: Feature_Value[] = []
    let selectedFeatureConfigs: Feature_Config[] = []
    let selectedFeatureValuesGroupedByGroupId: Dictionary<Feature_Value[]> = {}

    if (featureBaseWhere) {
      selectedFeaturesValues = data.feature_value
        ? overrideFeatureValues(sortFeatureValuesByFirstConfig(data.feature_value))
        : []

      selectedFeatureConfigs = overrideFeatureConfigs(
        selectedFeaturesValues.flatMap((x) => x.feature_sorting_configs).map((i) => i.feature_config),
      )

      selectedFeatureValuesGroupedByGroupId = groupBy(selectedFeaturesValues, (x) => x.feature_config.feature_group_id)
    }

    // general
    const uniqueFeaturesTypeConfigs = uniqBy(
      data.feature_type_sorting_config.map((i) => i.feature_type_config) ?? [],
      (item) => item.id,
    )
    const uniqueFeaturesGroupsConfigs = uniqBy(
      data.feature_group_sorting_config.map((i) => i.feature_group_config) ?? [],
      (item) => item.id,
    )
    const uniqueFeatureConfigs = overrideFeatureConfigs(
      uniqBy(data.feature_sorting_config.map((i) => i.feature_config) ?? [], (item) => item.id),
    )

    // features showed as available for selection
    const availableFeatureConfigs = differenceFeaturesList(uniqueFeatureConfigs, selectedFeatureConfigs) ?? []

    return {
      featuresTypeConfigs: uniqueFeaturesTypeConfigs,
      featureGroupConfigs: uniqueFeaturesGroupsConfigs,
      featureConfigs: uniqueFeatureConfigs,

      selectedFeatureValues: selectedFeaturesValues,
      selectedFeatureValuesGroupedByGroupId: selectedFeatureValuesGroupedByGroupId,

      availableFeatureConfigs: availableFeatureConfigs,
    }
  }

export const getNestedFeatureConfigData =
  () =>
  (featureData?: FeatureData): NestedFeatureData | undefined => {
    if (featureData) {
      const nestedGrouping = new Map<number, Map<number | null, Feature_Config[]>>()

      featureData.featuresTypeConfigs.forEach((ft) => {
        const typeGroups = new Map<number | null, Feature_Config[]>()

        featureData.featureGroupConfigs
          .filter((fg) => fg.feature_type_id === ft.id)
          .forEach((fg) => {
            typeGroups.set(
              fg.id,
              featureData.availableFeatureConfigs.filter(
                (config) => config.feature_type_id === ft.id && config.feature_group_id === fg.id,
              ),
            )
          })

        const standalone = featureData.availableFeatureConfigs.filter(
          (fc) => fc.feature_type_id === ft.id && fc.feature_group_id === null,
        )
        if (standalone.length) {
          typeGroups.set(null, standalone)
        }

        nestedGrouping.set(ft.id, typeGroups)
      })

      return {
        nestedFeatureData: nestedGrouping,
      }
    }
  }

const switchFeatureTypePosition =
  (urqlClient: Client) =>
  async (
    ft1: Feature_Type_Sorting_Config,
    ft2: Feature_Type_Sorting_Config,
    process?: PROCESS_TYPE,
    level?: LEVEL_TYPE,
  ) => {
    if (ft2.sort_order === ft1.sort_order) {
      return Promise.reject(new Error())
    }

    const setClause1: Feature_Type_Sorting_Config_Set_Input = {
      sort_order: ft2.sort_order,
    }

    const setClause2: Feature_Type_Sorting_Config_Set_Input = {
      sort_order: ft1.sort_order,
    }

    const updateFirstWhere: Feature_Type_Sorting_Config_Bool_Exp = {
      _and: [
        level ? { level: { _eq: level } } : {},
        process ? { process: { _eq: process } } : {},
        { feature_type_config_id: { _eq: ft1.feature_type_config_id } },
      ],
    }

    const updateSecondWhere: Feature_Type_Sorting_Config_Bool_Exp = {
      _and: [
        level ? { level: { _eq: level } } : {},
        process ? { process: { _eq: process } } : {},
        { feature_type_config_id: { _eq: ft2.feature_type_config_id } },
      ],
    }

    return urqlClient
      .mutation<
        {
          updateFirst: Mutation_Root['update_feature_type_sorting_config']
          updateSecond: Mutation_Root['update_feature_type_sorting_config']
        },
        {
          firstWhere: Feature_Type_Sorting_Config_Bool_Exp
          secondWhere: Feature_Type_Sorting_Config_Bool_Exp
          setClause1: Feature_Type_Sorting_Config_Set_Input
          setClause2: Feature_Type_Sorting_Config_Set_Input
        }
      >(updateSortOnFeatureTypeConfig, {
        firstWhere: updateFirstWhere,
        secondWhere: updateSecondWhere,
        setClause1: setClause1,
        setClause2: setClause2,
      })
      .toPromise()
  }

const switchFeatureGroupPosition =
  (urqlClient: Client) =>
  async (
    fg1: Feature_Group_Sorting_Config,
    fg2: Feature_Group_Sorting_Config,
    process?: PROCESS_TYPE,
    level?: LEVEL_TYPE,
  ) => {
    if (fg2.sort_order === fg1.sort_order) {
      return Promise.reject(new Error())
    }

    const setClause1: Feature_Group_Sorting_Config_Set_Input = {
      sort_order: fg2.sort_order,
    }

    const setClause2: Feature_Group_Sorting_Config_Set_Input = {
      sort_order: fg1.sort_order,
    }

    const updateFirstWhere: Feature_Group_Sorting_Config_Bool_Exp = {
      _and: [
        level ? { level: { _eq: level } } : {},
        process ? { process: { _eq: process } } : {},
        { feature_group_config_id: { _eq: fg1.feature_group_config_id } },
      ],
    }

    const updateSecondWhere: Feature_Group_Sorting_Config_Bool_Exp = {
      _and: [
        level ? { level: { _eq: level } } : {},
        process ? { process: { _eq: process } } : {},
        { feature_group_config_id: { _eq: fg2.feature_group_config_id } },
      ],
    }

    return urqlClient
      .mutation<
        {
          updateFirst: Mutation_Root['update_feature_group_sorting_config']
          updateSecond: Mutation_Root['update_feature_group_sorting_config']
        },
        {
          firstWhere: Feature_Group_Sorting_Config_Bool_Exp
          secondWhere: Feature_Group_Sorting_Config_Bool_Exp
          setClause1: Feature_Group_Sorting_Config_Set_Input
          setClause2: Feature_Group_Sorting_Config_Set_Input
        }
      >(updateSortOnFeatureGroupConfig, {
        firstWhere: updateFirstWhere,
        secondWhere: updateSecondWhere,
        setClause1: setClause1,
        setClause2: setClause2,
      })
      .toPromise()
  }

const switchFeaturePosition =
  (urqlClient: Client) =>
  async (f1: Feature_Sorting_Config, f2: Feature_Sorting_Config, process?: PROCESS_TYPE, level?: LEVEL_TYPE) => {
    if (f1.sort_order === f2.sort_order) {
      return Promise.reject(new Error())
    }

    const setClause1: Feature_Sorting_Config_Set_Input = {
      sort_order: f2.sort_order,
    }

    const setClause2: Feature_Sorting_Config_Set_Input = {
      sort_order: f1.sort_order,
    }

    const updateFirstWhere: Feature_Sorting_Config_Bool_Exp = {
      _and: [
        level ? { level: { _eq: level } } : {},
        process ? { process: { _eq: process } } : {},
        { feature_config_id: { _eq: f1.feature_config_id } },
      ],
    }

    const updateSecondWhere: Feature_Sorting_Config_Bool_Exp = {
      _and: [
        level ? { level: { _eq: level } } : {},
        process ? { process: { _eq: process } } : {},
        { feature_config_id: { _eq: f2.feature_config_id } },
      ],
    }

    return urqlClient
      .mutation<
        {
          updateFirst: Mutation_Root['update_feature_sorting_config']
          updateSecond: Mutation_Root['update_feature_sorting_config']
        },
        {
          firstWhere: Feature_Sorting_Config_Bool_Exp
          secondWhere: Feature_Sorting_Config_Bool_Exp
          setClause1: Feature_Sorting_Config_Set_Input
          setClause2: Feature_Sorting_Config_Set_Input
        }
      >(updateSortOnFeatureConfig, {
        firstWhere: updateFirstWhere,
        secondWhere: updateSecondWhere,
        setClause1: setClause1,
        setClause2: setClause2,
      })
      .toPromise()
  }

const overrideFeatureValues = (featureValues: Feature_Value[]): Feature_Value[] => {
  return featureValues.map((fv) => ({
    ...fv,
    feature_config: fv.feature_sorting_configs?.[0]?.feature_config ?? fv.feature_config,
  }))
}

const overrideFeatureConfigs = (featureConfig: Feature_Config[]): Feature_Config[] => {
  return featureConfig.map((fc) => ({
    ...fc,
    feature_type_config: fc.feature_type_sorting_configs?.[0]?.feature_type_config ?? fc.feature_type_config,
    feature_group_config: fc.feature_group_sorting_configs?.[0]?.feature_group_config ?? fc.feature_group_config,
  }))
}

function sortFeatureValuesByFirstConfig(featureValues: Feature_Value[]): Feature_Value[] {
  return [...featureValues].sort((a, b) => {
    const aSortOrder = a.feature_sorting_configs?.[0]?.sort_order
    const bSortOrder = b.feature_sorting_configs?.[0]?.sort_order
    return aSortOrder - bSortOrder
  })
}

export const useFeatureConfiguration = () => {
  const urqlClient = useClient()

  return {
    fetchAllFeatureConfigData: fetchAllFeatureConfigData(urqlClient),
    getNestedFeatureConfigData: getNestedFeatureConfigData(),
    switchFeatureTypePosition: switchFeatureTypePosition(urqlClient),
    switchFeatureGroupPosition: switchFeatureGroupPosition(urqlClient),
    switchFeaturePosition: switchFeaturePosition(urqlClient),
  }
}
